import {
  todayParis,
  reportingPeriod,
  isBusinessDay,
} from "../utils/calendar.mjs";
export { getScoreReasons } from "./commercial.mjs";
export { getNextActions, getNextBestAction } from "./prioritization.mjs";

export const defaultDailyGoals = {
  callResults: 24,
  decisionMakersReached: 8,
  needsDetected: 3,
  quotesSent: 2,
};
export const defaultWorkday = {
  start: "09:00",
  end: "18:00",
  lunchStart: "12:30",
  lunchEnd: "13:30",
};
const dayOf = (value) => (value ? todayParis(new Date(value)) : "");
export function getDailyGoals(state, userId, date = todayParis()) {
  const history = (state.goalHistory || [])
    .filter((g) => g.userId === userId && g.date <= date)
    .sort((a, b) => a.date.localeCompare(b.date));
  return {
    ...defaultDailyGoals,
    callResults: state.dailyCallGoal || 24,
    ...(state.dailyGoals || {}),
    ...(state.goalsByUser?.[userId] || {}),
    ...(history.at(-1)?.goals || {}),
  };
}
export function setDailyGoals(state, userId, goals, now = new Date()) {
  if (
    Object.keys(defaultDailyGoals).some(
      (key) =>
        !Number.isInteger(goals[key]) || goals[key] < 1 || goals[key] > 500,
    )
  )
    throw new Error(
      "Les quatre objectifs doivent être des nombres entiers de 1 à 500.",
    );
  const date = todayParis(now);
  const history = state.goalHistory || [];
  const baseline = history.some((g) => g.userId === userId)
    ? []
    : [
        {
          userId,
          date: "1970-01-01",
          goals: getDailyGoals(state, userId, date),
        },
      ];
  return {
    ...state,
    goalsByUser: { ...state.goalsByUser, [userId]: goals },
    goalHistory: [
      ...baseline,
      ...(state.goalHistory || []).filter(
        (g) => g.userId !== userId || g.date !== date,
      ),
      { userId, date, goals },
    ],
  };
}
export function userDailyState(state, userId) {
  const ids = new Set(
    state.clients.filter((c) => c.ownerId === userId).map((c) => c.id),
  );
  const owned = (item) =>
    item.ownerId ? item.ownerId === userId : ids.has(item.clientId);
  return {
    ...state,
    clients: state.clients.filter((c) => ids.has(c.id)),
    tasks: state.tasks.filter(owned),
    results: state.results.filter(row=>!row.voidedAt).filter(owned),
    events: state.events.filter(owned),
    needs: state.needs.filter(owned),
    quotes: state.quotes.filter(owned),
    engagements: state.engagements.filter(owned),
    signals: state.signals.filter((s) => ids.has(s.clientId)),
  };
}
export function periodIndicators(state, from, to) {
  const inPeriod = (value) =>
    value && dayOf(value) >= from && dayOf(value) <= to;
  const results = state.results.filter(row=>!row.voidedAt).filter((r) => inPeriod(r.createdAt)),
    events = state.events.filter((e) => inPeriod(e.createdAt));
  return {
    callResults: results.length,
    clientVisits: state.tasks.filter(t => t.done && inPeriod(t.completedAt) && /visite/i.test(String(t.type || t.title || ""))).length,
    decisionMakersReached: results.filter((r) => r.deciderJoined).length,
    contactsCreated: events.filter((e) => e.type === "contact_created").length,
    needsDetected: state.needs.filter((n) => inPeriod(n.createdAt)).length,
    quotesSent: state.quotes.filter((q) => inPeriod(q.sentAt)).length,
    quotesSigned: state.quotes.filter((q) => inPeriod(q.signedAt)).length,
    clientsReactivated: new Set(
      events
        .filter((e) => e.type === "client_reactivated")
        .map((e) => e.clientId),
    ).size,
    tasksCompleted: state.tasks.filter((t) => t.done && inPeriod(t.completedAt))
      .length,
    followUps: state.tasks.filter(
      (t) => t.category === "follow" && t.done && inPeriod(t.completedAt),
    ).length,
    emailsSent: events.filter(
      (e) => e.type === "brochure_sent" || e.type === "quote_sent",
    ).length,
  };
}
const minutes = (time) => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};
export function calculateDailyPace(
  actual,
  goal,
  now = new Date(),
  config = defaultWorkday,
) {
  const clock = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Paris",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(now);
  const start = minutes(config.start),
    end = minutes(config.end),
    current = minutes(clock);
  const pauseStart = config.lunchStart
    ? Math.max(start, minutes(config.lunchStart))
    : end;
  const pauseEnd = config.lunchEnd
    ? Math.min(end, minutes(config.lunchEnd))
    : end;
  const pause = Math.max(0, pauseEnd - pauseStart),
    total = end - start - pause;
  const elapsed = Math.max(
    0,
    Math.min(end, current) -
      start -
      Math.max(0, Math.min(current, pauseEnd) - pauseStart),
  );
  const expected =
    total > 0 ? Math.min(goal, Math.floor((goal * elapsed) / total)) : 0;
  return {
    expected,
    actual,
    difference: actual - expected,
    afternoon: current >= 14 * 60,
    clock,
  };
}
export function progressionMessage(actual, goal) {
  const ratio = actual / goal,
    remaining = Math.max(0, goal - actual);
  if (ratio > 1) return `Objectif dépassé de ${actual - goal} résultats.`;
  if (ratio === 1) return "✓ Objectif atteint.";
  if (ratio >= 0.7)
    return `Plus que ${remaining} résultats pour atteindre l’objectif.`;
  if (ratio >= 0.3)
    return `Progression en cours. Encore ${remaining} résultats à saisir.`;
  return "La journée démarre — prochaine priorité prête.";
}
export function calculateCommercialPotential(state, date = todayParis()) {
  const active = state.needs.filter(
    (n) => !["perdu", "suspendu", "signé"].includes(n.status),
  );
  const latest = active
    .map(
      (n) =>
        state.quotes
          .filter(
            (q) => q.needId === n.id && !["refusé", "signé"].includes(q.status),
          )
          .sort((a, b) => b.version - a.version)[0],
    )
    .filter(Boolean);
  const amounts = latest.filter((q) => Number(q.amount) > 0);
  const month = date.slice(0, 7);
  return {
    activeCount: active.length,
    estimatedCA: amounts.length
      ? amounts.reduce((sum, q) => sum + Number(q.amount), 0)
      : null,
    quotesInProgress: latest
      .filter((q) => ["envoyé", "négociation"].includes(q.status))
      .reduce((sum, q) => sum + Number(q.amount || 0), 0),
    recoveredCA: state.clients.reduce(
      (sum, c) =>
        sum +
        (c.reactivatedAt
          ? c.orders
              .filter(
                (o) =>
                  o.date >= c.reactivatedAt.slice(0, 10) &&
                  o.date.startsWith(month),
              )
              .reduce((n, o) => n + Number(o.amount || 0), 0)
          : 0),
      0,
    ),
    knownAmounts: amounts.length,
  };
}
export function personalRegularity(state, userId, date = todayParis()) {
  const counts = new Map();
  for (const r of state.results.filter(row=>!row.voidedAt)) {
    const d = dayOf(r.createdAt);
    if (d <= date) counts.set(d, (counts.get(d) || 0) + 1);
  }
  const achievedDays = [...counts]
    .filter(
      ([d, count]) => count >= getDailyGoals(state, userId, d).callResults,
    )
    .map(([d]) => d)
    .sort();
  const achieved = new Set(achievedDays),
    closures = state.calendar?.closures || [];
  const previousWorkday = (d) => {
    let p = new Date(`${d}T12:00:00Z`);
    do {
      p.setUTCDate(p.getUTCDate() - 1);
    } while (!isBusinessDay(p.toISOString().slice(0, 10), closures));
    return p.toISOString().slice(0, 10);
  };
  let bestStreak = 0,
    streak = 0,
    last = null;
  for (const d of achievedDays) {
    streak = last === previousWorkday(d) ? streak + 1 : 1;
    bestStreak = Math.max(bestStreak, streak);
    last = d;
  }
  let currentStreak = 0,
    cursor = date;
  if (!achieved.has(cursor)) cursor = previousWorkday(cursor);
  while (achieved.has(cursor)) {
    currentStreak++;
    cursor = previousWorkday(cursor);
  }
  const week = reportingPeriod("week", date);
  return {
    achievedDays,
    currentStreak,
    bestStreak,
    weekCount: achievedDays.filter((d) => d >= week.from && d <= week.to)
      .length,
  };
}
export function dailyAlerts(state, date) {
  return [
    {
      key: "quotes",
      label: "Devis envoyés sans relance",
      items: state.quotes.filter(
        (q) =>
          ["envoyé", "négociation"].includes(q.status) &&
          !state.needs.some(
            (n) =>
              n.id === q.needId &&
              ["signé", "perdu", "suspendu"].includes(n.status),
          ) &&
          !state.quotes.some(
            (other) =>
              other.needId === q.needId &&
              other.version > q.version &&
              ["envoyé", "négociation", "signé"].includes(other.status),
          ) &&
          !state.tasks.some((t) => !t.done && t.quoteId === q.id),
      ),
      kind: "quotes",
    },
    {
      key: "engagements",
      label: "Engagements en retard",
      items: state.engagements.filter((e) => !e.done && e.due < date),
      kind: "engagements",
    },
    {
      key: "signals",
      label: "Anciens clients avec nouveau signal",
      items: state.clients.filter(
        (c) =>
          c.kind !== "prospect" &&
          state.signals.some(
            (s) =>
              s.clientId === c.id &&
              s.status === "nouveau" &&
              s.date <= date &&
              Date.parse(date) - Date.parse(s.date) <= 30 * 86400000,
          ),
      ),
      kind: "clients",
    },
    {
      key: "decline",
      label: "Clients en baisse d’activité",
      items: state.clients.filter(
        (c) =>
          /baisse/i.test(c.riskReason || "") ||
          c.riskReasons?.some((r) => /Baisse du/.test(r)),
      ),
      kind: "clients",
    },
  ].filter((a) => a.items.length);
}
